/**
 * useMotivoAnulacionFactura · EL desplegable único de motivos para retroceder (anular) una factura.
 *
 * Ann 30-10-2026: «cuando retrocedo una factura no me da opciones, me dice que lo anote a mano;
 * pon un desplegable de opciones y evitamos errores».
 *
 * Catálogo: lo publica el servidor (GET /facturacion/motivos-anulacion) desde
 * backend/services/motivos_anulacion_factura.py. Aquí NO hay lista propia: si falta un motivo,
 * se añade allí y aparece en todas las pantallas a la vez. El endpoint de anular rechaza (400)
 * cualquier texto que no salga del catálogo.
 *
 * Uso (en cualquier pantalla):
 *   const { pedirMotivo, modalMotivo } = useMotivoAnulacionFactura();
 *   ...
 *   const motivo = await pedirMotivo("MPD-1095");   // null si cancela
 *   if (!motivo) return;
 *   await axios.post(`${API}/facturacion/facturas/${id}/anular?motivo=${encodeURIComponent(motivo)}`);
 *   ...
 *   return (<> ... {modalMotivo} </>);
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../../api';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

let _catalogoCache = null;   // una sola carga por sesión

export async function cargarMotivosAnulacion() {
  if (_catalogoCache) return _catalogoCache;
  const r = await axios.get(`${API}/facturacion/motivos-anulacion`);
  _catalogoCache = r.data || { motivos: [], codigo_otro: 'otro', min_detalle_otro: 5 };
  return _catalogoCache;
}

/** Texto final que viaja al servidor: la etiqueta del catálogo, o «Otro: detalle». */
export function textoMotivo(catalogo, codigo, detalle) {
  if (!catalogo || !codigo) return null;
  if (codigo === catalogo.codigo_otro) {
    const d = (detalle || '').trim();
    return d.length >= (catalogo.min_detalle_otro || 5) ? `Otro: ${d}` : null;
  }
  const m = (catalogo.motivos || []).find((x) => x.codigo === codigo);
  return m ? m.etiqueta : null;
}

export default function useMotivoAnulacionFactura() {
  const [abierto, setAbierto] = useState(false);
  const [etiquetaFactura, setEtiquetaFactura] = useState('');
  const [catalogo, setCatalogo] = useState(_catalogoCache);
  const [codigo, setCodigo] = useState('');
  const [detalle, setDetalle] = useState('');
  const resolverRef = useRef(null);

  useEffect(() => {
    if (abierto && !catalogo) {
      cargarMotivosAnulacion().then(setCatalogo).catch(() => setCatalogo({ motivos: [], codigo_otro: 'otro', min_detalle_otro: 5 }));
    }
  }, [abierto, catalogo]);

  const cerrar = useCallback((valor) => {
    setAbierto(false);
    const r = resolverRef.current; resolverRef.current = null;
    if (r) r(valor);
  }, []);

  /** Abre el desplegable y devuelve el motivo elegido (string) o null si se cancela. */
  const pedirMotivo = useCallback((etiqueta) => {
    setEtiquetaFactura(etiqueta || '');
    setCodigo(''); setDetalle('');
    setAbierto(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const esOtro = catalogo && codigo === catalogo.codigo_otro;
  const motivoFinal = textoMotivo(catalogo, codigo, detalle);

  const modalMotivo = (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) cerrar(null); }}>
      <DialogContent className="max-w-md" data-testid="modal-motivo-anulacion">
        <DialogHeader>
          <DialogTitle>Retroceder factura {etiquetaFactura}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Se anula la factura y se emite su abono. El motivo queda en la cadena fiscal.
          </p>
          <div>
            <Label htmlFor="sel-motivo-anulacion">Motivo</Label>
            <select
              id="sel-motivo-anulacion"
              data-testid="sel-motivo-anulacion"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              disabled={!catalogo}
            >
              <option value="">{catalogo ? 'Elige un motivo…' : 'Cargando motivos…'}</option>
              {(catalogo?.motivos || []).map((m) => (
                <option key={m.codigo} value={m.codigo}>{m.etiqueta}</option>
              ))}
            </select>
          </div>
          {esOtro && (
            <div>
              <Label htmlFor="txt-motivo-otro">Detalle (mínimo {catalogo.min_detalle_otro || 5} letras)</Label>
              <Textarea
                id="txt-motivo-otro"
                data-testid="txt-motivo-otro"
                className="mt-1"
                rows={3}
                value={detalle}
                onChange={(e) => setDetalle(e.target.value)}
                placeholder="Escribe el motivo"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => cerrar(null)}>Cancelar</Button>
          <Button
            type="button"
            data-testid="btn-confirmar-motivo-anulacion"
            disabled={!motivoFinal}
            onClick={() => cerrar(motivoFinal)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Retroceder factura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { pedirMotivo, modalMotivo };
}
